import type { ComponentType } from "react";

import type {
  AnyDDObjectTypeDefinition,
  DDObjectEditorProps,
  DDObjectId,
  DDObjectModellerProps,
  DominoExpansion,
  DominoRowCol,
} from "./base";
import type { Bounds } from "../types";
import type { DominoColorClipboardItem } from "../dominoes/clipboardItem";
import type { DominoSelectionEntry } from "../dominoes/selectionStore";
import { buildPlaneDefinition, type BuildPlaneDDObject } from "./buildPlane/object-model";
import { fieldElementDefinition, type FieldElementDDObject } from "./fieldElement/object-model";

// ── Register DDObject types here (name → definition module) ──
// Adding a type: create an object-types/<name>.ts implementing
// DDObjectTypeDefinition, then add it to this map and to the DDObject union below.
export const DD_OBJECT_TYPES = {
  buildPlane: buildPlaneDefinition,
  fieldElement: fieldElementDefinition,
} satisfies Record<string, AnyDDObjectTypeDefinition>;

/** Union of all registered DDObject-type names. */
export type DDObjectType = keyof typeof DD_OBJECT_TYPES;

/** Union of all concrete DDObject shapes — one member per registered type. */
export type DDObject = BuildPlaneDDObject | FieldElementDDObject;

// ── Registry-driven accessors — no per-type switch anywhere else ──

export const getDDObjectIcon = (type: DDObjectType) => DD_OBJECT_TYPES[type].icon;

export const getDDObjectDefaultName = (type: DDObjectType) =>
  DD_OBJECT_TYPES[type].defaultName;

export const createDDObject = (type: DDObjectType, id: DDObjectId): DDObject =>
  DD_OBJECT_TYPES[type].create(id) as DDObject;

/**
 * The property editor to render for a DDObject of this type. The cast undoes
 * the registry's type erasure (see AnyDDObjectTypeDefinition); callers pass the
 * matching DDObject, which the definition's own typing already guarantees.
 */
export const getDDObjectEditor = (type: DDObjectType) =>
  DD_OBJECT_TYPES[type].editor as ComponentType<DDObjectEditorProps<DDObject>>;

/**
 * The three.js modeller for a DDObject of this type, or undefined for types with
 * no visual representation yet. Cast as per getDDObjectEditor.
 */
export const getDDObjectModeller = (type: DDObjectType) =>
  DD_OBJECT_TYPES[type].modeller as
    | ComponentType<DDObjectModellerProps<DDObject>>
    | undefined;

/**
 * This DDObject's footprint on the build plane, or undefined if its type declares
 * none (it has no spatial extent the camera can use). Takes the DDObject, not the
 * type, because the footprint depends on the instance's values. The cast undoes
 * the registry's type erasure, same as getDDObjectEditor: indexing by a union
 * `type` otherwise intersects the per-type bounds params down to `never`.
 */
export const getDDObjectBounds = (ddObject: DDObject): Bounds | undefined => {
  const bounds = DD_OBJECT_TYPES[ddObject.type].bounds as
    | ((ddObject: DDObject) => Bounds | undefined)
    | undefined;
  return bounds?.(ddObject);
};

/**
 * Whether this DDObject can be selected and directly manipulated. A type opts
 * out with `selectable: false` (the root BuildPlane does); callers that draw
 * the selection overlay additionally require `getDDObjectBounds` to be defined.
 */
export const isDDObjectSelectable = (ddObject: DDObject): boolean =>
  DD_OBJECT_TYPES[ddObject.type].selectable !== false;

/**
 * Whether this DDObject exposes a domino-editing mode (double-click to
 * select/arrange its dominoes). A type opts in with `dominoEditable: true`
 * (fieldElement does); defaults to false.
 */
export const isDominoEditable = (ddObject: DDObject): boolean =>
  DD_OBJECT_TYPES[ddObject.type].dominoEditable === true;

/**
 * How far this DDObject's dominoes grow while domino editing mode's Expand
 * toggle is on, or undefined if its type declares no `dominoExpansion` or this
 * instance has no room to grow — which is also what disables the Expand button.
 * Cast as per getDDObjectBounds.
 */
export const getDominoExpansion = (ddObject: DDObject): DominoExpansion | undefined => {
  const fn = DD_OBJECT_TYPES[ddObject.type].dominoExpansion as
    | ((ddObject: DDObject) => DominoExpansion | undefined)
    | undefined;
  return fn?.(ddObject);
};

/**
 * A stable cell-id function for this DDObject, or undefined if its type
 * declares no `dominoCellId` (colors can't be preserved across a regenerate
 * for that type). Bound to this specific instance so callers just pass a
 * flat domino index. Cast as per getDDObjectBounds.
 */
export const getDominoCellId = (
  ddObject: DDObject,
): ((flatIndex: number) => number) | undefined => {
  const fn = DD_OBJECT_TYPES[ddObject.type].dominoCellId as
    | ((ddObject: DDObject, flatIndex: number) => number)
    | undefined;
  return fn ? (flatIndex: number) => fn(ddObject, flatIndex) : undefined;
};

/**
 * This DDObject's row/column-like mapping, or undefined if its type declares
 * none (it takes no part in cross-element color paste). Bound to the instance,
 * so callers just pass a flat domino index. Cast as per getDDObjectBounds.
 */
export const getDominoRowCol = (
  ddObject: DDObject,
): ((flatIndex: number) => DominoRowCol) | undefined => {
  const fn = DD_OBJECT_TYPES[ddObject.type].dominoRowCol as
    | ((ddObject: DDObject, flatIndex: number) => DominoRowCol)
    | undefined;
  return fn ? (flatIndex: number) => fn(ddObject, flatIndex) : undefined;
};

/**
 * The inverse of getDominoRowCol for this DDObject — undefined outside its live
 * dominoes. A type declaring one of the pair should declare both; see
 * `dominoRowCol`'s contract in base.ts.
 */
export const getDominoIndexAt = (
  ddObject: DDObject,
): ((row: number, col: number) => number | undefined) | undefined => {
  const fn = DD_OBJECT_TYPES[ddObject.type].dominoIndexAt as
    | ((ddObject: DDObject, row: number, col: number) => number | undefined)
    | undefined;
  return fn ? (row: number, col: number) => fn(ddObject, row, col) : undefined;
};

/**
 * How this DDObject snaps a point onto its own grid, or undefined when its type
 * has no grid to snap to — see `snapShapePoint`'s contract in base.ts.
 * If undefined, the point is resolved to itself, so shapes can simply
 * call the function unconditionally.
 */
export const getSnapShapePoint = (
  ddObject: DDObject,
): ((x: number, y: number) => { x: number; y: number }) | undefined => {
  const fn = DD_OBJECT_TYPES[ddObject.type].snapShapePoint as
    | ((ddObject: DDObject, x: number, y: number) => { x: number; y: number })
    | undefined;
  return fn ? (x: number, y: number) => fn(ddObject, x, y) : undefined;
};

/**
 * The build-plane point this DDObject's dominoes keep their positions against,
 * or undefined when its type declares none — see `dominoLayoutAnchor`'s contract
 * in base.ts. Callers fall back to the DDObject's bounds() corner;
 * image-map/object-model.ts's imageOriginFor is the one place that does so, and
 * every consumer of the image's coordinates goes through it.
 */
export const getDominoLayoutAnchor = (
  ddObject: DDObject,
): { x: number; y: number } | undefined => {
  const fn = DD_OBJECT_TYPES[ddObject.type].dominoLayoutAnchor as
    | ((ddObject: DDObject) => { x: number; y: number })
    | undefined;
  return fn?.(ddObject);
};

/**
 * This type's override of how copied domino colors land on it, or undefined —
 * which is the normal case, meaning "use the generic row/col paste". Resolved
 * by dominoes/rowColPaste.ts's resolveDominoColorPaste rather than called
 * directly; nothing declares an override today.
 */
export const getPasteDominoColorsOverride = (
  ddObject: DDObject,
):
  | ((
      item: DominoColorClipboardItem,
      selection: DominoSelectionEntry,
    ) => Array<[index: number, colorId: number]> | undefined)
  | undefined => {
  const fn = DD_OBJECT_TYPES[ddObject.type].pasteDominoColors as
    | ((
        ddObject: DDObject,
        item: DominoColorClipboardItem,
        selection: DominoSelectionEntry,
      ) => Array<[index: number, colorId: number]> | undefined)
    | undefined;
  return fn ? (item, selection) => fn(ddObject, item, selection) : undefined;
};

/**
 * The store patch that makes `ddObject` occupy `bounds` (cursor move/resize), or
 * undefined if its type declares no `setBounds`, or if `bounds` was too
 * small/invalid. Takes the instance, and casts to undo the registry's type
 * erasure, exactly like getDDObjectBounds.
 */
export const applyDDObjectBounds = (
  ddObject: DDObject,
  bounds: Bounds,
): Partial<DDObject> | undefined => {
  const setBounds = DD_OBJECT_TYPES[ddObject.type].setBounds as
    | ((ddObject: DDObject, bounds: Bounds) => Partial<DDObject> | undefined)
    | undefined;
  return setBounds?.(ddObject, bounds);
};

/**
 * The creation patch for a region drawn on the build plane, or undefined if
 * this type doesn't support region placement, or if `region` was too
 * small/invalid for it. Cast as per getDDObjectBounds: indexing by a union
 * `type` otherwise intersects the per-type params down to `never`.
 */
export const getDDObjectCreateFromRegion = (
  type: DDObjectType,
  region: Bounds,
): Partial<DDObject> | undefined => {
  const createFromRegion = DD_OBJECT_TYPES[type].createFromRegion as
    | ((region: Bounds) => Partial<DDObject> | undefined)
    | undefined;
  return createFromRegion?.(region);
};